import React, { useContext as useContextCore } from 'react'

import { OrderStatusEnum } from '@ztechapps/common.contract'
import { humanize } from '@ztechapps/common.utils'
import { TabName } from './TabName'
import { useOrderList } from './useOrderList'

export type ProviderProps = {
  children: React.ReactNode
}

export type ContextType = {
  filter: Record<any, any>
  getTabTitle: (name: TabName) => string
  orderLists: Record<TabName, ReturnType<typeof useOrderList>>
} & Omit<ProviderProps, 'children'>

const context = React.createContext<ContextType>({} as ContextType)
const CoreProvider = context.Provider

export const OrderListProvider = ({ children, ...props }) => {
  const filter = {}

  const orderLists: Record<TabName, ReturnType<typeof useOrderList>> = {
    [TabName.all]: useOrderList({ filter, tabName: TabName.all }),
    [TabName.delivered]: useOrderList({
      filter: { status: OrderStatusEnum.Delivered },
      tabName: TabName.delivered,
    }),
    [TabName.shipping]: useOrderList({
      filter: { status: { $in: [OrderStatusEnum.InTransit, OrderStatusEnum.Shipped] } },
      tabName: TabName.shipping,
    }),
    [TabName.quotation]: useOrderList({
      filter: {
        status: { $in: [OrderStatusEnum.Quotation] },
      },
      tabName: TabName.quotation,
    }),
    [TabName.canceled]: useOrderList({
      filter: { status: { $in: [OrderStatusEnum.Cancelled] } },
      tabName: TabName.canceled,
    }),
    [TabName.inCart]: useOrderList({
      filter: { status: { $in: [OrderStatusEnum.InCart] } },
      tabName: TabName.inCart,
    }),
    [TabName.order]: useOrderList({
      filter: {
        status: {
          $in: [
            OrderStatusEnum.ReadyForPayment,
            OrderStatusEnum.Order,
            OrderStatusEnum.InProgress,
            OrderStatusEnum.Paid,
            OrderStatusEnum.Packing,
          ],
        },
      },
      tabName: TabName.inCart,
    }),
  }

  const getTabTitle = (name: TabName) => {
    const count = orderLists[name].count
    return `${humanize(name)} ${orderLists[name].count ? `(${count})` : ''} `
  }

  return <CoreProvider value={{ ...props, filter, getTabTitle, orderLists }}>{children}</CoreProvider>
}

export const useOrderListContext = () => useContextCore(context)
